import { CatalogueCode, CodeUoM, ExpandedCatalogueCode } from '@jms/model';

/** One cart row in the item browser — code plus qty/UoM/location chosen before add. */
export interface ItemBrowserCartEntry {
    code: CatalogueCode;
    /** null while Quantity is blank or not a positive number — Add stays off until every row has a real qty. */
    quantity: number | null;
    codeUoM?: CodeUoM;
    /** Item location for this line. Stays undefined on a mount that does not show the picker. */
    location?: string;
    /** Stamped once at selection — see uomSelectOptions for why it is not derived in the template. */
    uomOptions: UoMSelectOption[];
}

export interface UoMSelectOption {
    key: string;
    name: string;
}

/** Dialog result: catalogue code stamped with cart qty/UoM/location for line mappers. */
export type ItemBrowserResult = CatalogueCode & {
    quantity: number;
    codeUoM?: CodeUoM;
};

/**
 * The `category` filter value the catalogue picker matches exactly against each item's stored
 * `category.name`. A sub-category row's key is that stored path as the server emits it, so it is sent
 * as is: rebuilding `"Category | Sub"` from the row's label drops leading or trailing whitespace
 * the label is trimmed of, and the list opens empty (WBS-12161). The General row (`null`) has no
 * path of its own and filters by the category name.
 */
export function categoryFilterValue(categoryName: string, subCategoryPath: string | null): string {

    return subCategoryPath ?? categoryName;
}

/**
 * KIT is a container, not a stocked unit. A leftover `baseUoM` from a previous type (WBS-8897)
 * must not become the cart's EACH default — Settings already hides the field for this type.
 */
function codeSupportsUoM(code: Pick<CatalogueCode, 'type'>): boolean {

    return code.type !== 'KIT';
}

/**
 * The code's own unit. Shape mirrors the legacy picker endpoint (`getUoMsByCodeName` in
 * code.repository.ts), which is what the classic Items tab persists: the base entry carries the
 * UoM's own id in the CodeUoM `_id` slot. Additional entries have their own sub-document uuid, so
 * the two id-spaces never collide, and code that dedupes a line's UoM against the catalogue list by
 * `_id` (resolveItemUoms) can match a base unit instead of always treating it as an extra.
 */
export function defaultCodeUoM(code: CatalogueCode): CodeUoM | undefined {

    if (!codeSupportsUoM(code)) return undefined;
    if (code.baseUoM) return { _id: code.baseUoM._id, uom: code.baseUoM, conversionRate: 1, group: 'Base' };
    return code.uoms?.[0];
}

export function codeUoMOptions(code: CatalogueCode): CodeUoM[] {

    if (!codeSupportsUoM(code)) return [];

    const options: CodeUoM[] = [];
    const seen = new Set<string>();
    const push = (entry?: CodeUoM) => {

        const key = entry?.uom?._id || entry?.uom?.name || entry?.uom?.abbreviation;
        if (!entry?.uom || !key || seen.has(key)) return;
        seen.add(key);
        options.push(entry);
    };

    push(defaultCodeUoM(code));
    for (const uom of code.uoms ?? []) push(uom);
    return options;
}

/**
 * Select-ready UoM options for a code. Stamped onto the cart entry rather than derived in the
 * template: `.map()` returns a fresh array every call, and wb-select takes `items` as a signal input
 * whose effect calls `itemsList.setItems()` on every reference change. A template call would hand it
 * a new reference on every change detection pass, rebuilding the list under the open dropdown.
 * A code's UoM set never changes for the life of an entry, so computing it once is also correct.
 */
export function uomSelectOptions(code: CatalogueCode): UoMSelectOption[] {

    return codeUoMOptions(code).map(option => ({
        key: codeUoMKey(option),
        name: uomLabel(option),
    }));
}

/**
 * How a UoM is spelled anywhere in the browser — the table column and the cart must not disagree.
 * `||` not `??`: an empty name falls through to the abbreviation, which `??` would keep as blank.
 */
export function uomLabel(codeUoM?: CodeUoM): string {

    return codeUoM?.uom?.name || codeUoM?.uom?.abbreviation || '—';
}

export function codeUoMKey(codeUoM?: CodeUoM): string {

    return codeUoM?.uom?._id || codeUoM?.uom?.name || codeUoM?.uom?.abbreviation || '';
}

/** Quantity is required and must be a real positive amount — blank, zero and NaN all fail. */
export function isValidItemQuantity(quantity: unknown): quantity is number {

    return typeof quantity === 'number' && Number.isFinite(quantity) && quantity > 0;
}

/** Cart input → qty, or null so a cleared field is not silently written as 1. */
export function parseItemQuantity(raw: string | number): number | null {

    if (raw === '' || raw == null) return null;
    const parsed = typeof raw === 'number' ? raw : Number(raw);
    return isValidItemQuantity(parsed) ? parsed : null;
}

export function cartQuantitiesAreValid(entries: readonly Pick<ItemBrowserCartEntry, 'quantity'>[]): boolean {

    return entries.length > 0 && entries.every(entry => isValidItemQuantity(entry.quantity));
}

export function toItemBrowserResults(entries: readonly ItemBrowserCartEntry[]): ItemBrowserResult[] {

    return entries.map(entry => ({
        ...entry.code,
        quantity: isValidItemQuantity(entry.quantity) ? entry.quantity : 0,
        codeUoM: entry.codeUoM,
        // The cart is the only source: a catalogue code carries no location of its own (the Code
        // collection has no such path), so there is nothing to fall back to.
        location: entry.location,
    }));
}

/**
 * Location payload for one add-items line. Omitted rather than sent empty: a tenant with
 * `items.hideLocation` rejects the field outright, and a cart row the user never located has nothing
 * to say about it. Every add service that persists a cart line goes through this.
 */
export function itemLocationPatch(selection: { location?: string }): { location?: string } {

    const location = selection.location?.trim();
    return location ? { location } : {};
}

/**
 * Re-apply qty/UoM after expandForDocument. Keeps **selected** order (not expand response order).
 *
 * One selection can yield several lines: a BUNDLE expands to its parent plus its children, a KIT to
 * its children alone (the parent is a container, never a line — matching the legacy quote editor,
 * which drops KIT parents after expanding). Children are found by `parentId`, the only link back to
 * the picked composite, since each child carries its own catalogue `_id`. Keying the whole merge by
 * `_id` is what silently reduced every composite to a single raw row (WBS-11456).
 *
 * The cart's qty/UoM answer is about the thing the user picked, so it lands on the parent (or on a
 * leaf, which is its own parent). A child's quantity is its per-parent quantity times the parent's,
 * mirroring `CodeUtility.createKitChildCodes`; a child's UoM is the one the composite recorded, not
 * the cart's.
 */
export function mergeItemBrowserResults(expanded: readonly ExpandedCatalogueCode[], selected: readonly ItemBrowserResult[]): ItemBrowserResult[] {

    const parentsById = new Map(expanded.filter(code => !code.parentId).map(code => [code._id, code]));
    const childrenByParentId = new Map<string, ExpandedCatalogueCode[]>();

    for (const code of expanded) {
        if (!code.parentId) continue;
        const siblings = childrenByParentId.get(code.parentId) ?? [];
        siblings.push(code);
        childrenByParentId.set(code.parentId, siblings);
    }

    return selected.flatMap(prior => {

        const parent = parentsById.get(prior._id);
        const children = childrenByParentId.get(prior._id) ?? [];
        const quantity = prior.quantity ?? parent?.quantity ?? 1;
        // An unexpanded selection still has to produce its row, or the pick silently vanishes.
        const parentLines = parent || !children.length
            ? [{ ...(parent ?? prior), quantity, codeUoM: prior.codeUoM ?? defaultCodeUoM(parent ?? prior) }]
            : [];

        const childLines = children.map(child => ({
            ...child,
            quantity: (child.quantity ?? 1) * quantity,
            codeUoM: resolveCatalogueCodeUoM(child),
        }));

        return [...parentLines, ...childLines];
    });
}

export function resolveCatalogueCodeUoM(code: CatalogueCode & { codeUoM?: CodeUoM }): CodeUoM | undefined {

    return code.codeUoM ?? defaultCodeUoM(code);
}
